import { CapTableSummary } from "@/components/cap-table/CapTableSummary";
import { InvestorProfile } from "@/components/cap-table/InvestorProfile";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Investor {
  name: string;
  image: string;
  affiliations: string[];
  roles: string[];
  linkedin: string;
  relevance: string;
}

interface CapTableEntry {
  name: string;
  percentage: number;
  shares: number;
  color: string;
}

const CapTable = () => {
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [capTableData, setCapTableData] = useState<CapTableEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      // Fetch investors
      const { data: investorsData, error: investorsError } = await supabase
        .from('investors')
        .select('name, image, affiliations, roles, linkedin, relevance');
      
      if (investorsError) {
        console.error('Error fetching investors:', investorsError);
        return;
      }

      // Fetch cap table entries
      const { data: capTableEntries, error: capTableError } = await supabase
        .from('cap_table_entries')
        .select('name, percentage, shares, color');

      if (capTableError) {
        console.error('Error fetching cap table entries:', capTableError);
        return;
      }

      // Add Mamduh Allam to investors data
      const mamduhAllam = {
        name: 'Mamduh Allam',
        image: '/lovable-uploads/f8c7d2bb-ea55-4b34-bba2-9ca8cd238453.png',
        affiliations: ['Splunk', 'Microsoft', 'F5', 'Cisco'],
        roles: ['Area Vice President at Splunk', 'Technology Sector Advisor', 'Former Microsoft Director'],
        linkedin: 'https://www.linkedin.com/in/mamduh/',
        relevance: 'Brings extensive enterprise technology expertise and regional market knowledge. His leadership experience across major technology companies and deep understanding of digital transformation in the Middle East makes him invaluable for Supplyfied\'s growth strategy and enterprise customer acquisition.'
      };

      // Add Saud Al-Shiha to investors data
      const saudAlShiha = {
        name: 'Saud Al-Shiha',
        image: '/lovable-uploads/7515f249-a8ac-4a70-97a4-b40eea8dbf4e.png',
        affiliations: ['Oracle', 'SBM', 'IBM'],
        roles: ['Technology Country Director at Oracle', 'Former Territory Manager Financial Technology', 'IBM Software Sales Specialist'],
        linkedin: 'https://www.linkedin.com/in/saud1st/',
        relevance: 'Brings extensive enterprise technology leadership in Saudi Arabia with deep expertise in Oracle solutions, financial technology, and digital transformation. His extensive experience in people management, negotiation, and strategic sales across FSI, Telco, Oil & Gas, and Electricity sectors provides invaluable market insights for Supplyfied\'s enterprise expansion in the region.'
      };

      // Add Thamer Alharbi to investors data
      const thamerAlharbi = {
        name: 'Thamer Alharbi',
        image: '/lovable-uploads/4c4e30ac-49b7-4f4e-8a88-5239c8a333b1.png',
        affiliations: ['Elm Company', 'Masdr', 'Microsoft', 'Oracle'],
        roles: ['Board Member at Elm Company', 'Board Member at Masdr', 'Former President - Microsoft Arabia and GM ME North', 'Former Vice President - Saudi and Upper Gulf at Oracle'],
        linkedin: 'https://www.linkedin.com/in/thameralharbi/',
        relevance: 'Brings exceptional leadership experience across major technology corporations and government entities in Saudi Arabia. His board positions at Elm Company and Masdr, combined with his executive roles at Microsoft and Oracle, provide invaluable strategic insights for Supplyfied\'s growth in the regional enterprise market and government sector digitization initiatives.'
      };

      const additionalInvestors = [mamduhAllam, saudAlShiha, thamerAlharbi];

      if (investorsData) {
        setInvestors([...investorsData, ...additionalInvestors]);
      } else {
        setInvestors(additionalInvestors);
      }

      if (capTableEntries) {
        setCapTableData(capTableEntries);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalShares = capTableData.reduce((acc, item) => acc + item.shares, 0);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <p className="text-gray-500">Loading data...</p>
        </div>
      ) : (
        <>
          <CapTableSummary capTableData={capTableData} totalShares={totalShares} />
          <InvestorProfile investors={investors} />
        </>
      )}
    </div>
  );
};

export default CapTable;
